import getGlobals from '../helpers/getGlobals'
// Uses the platform-native `fetch` (browser global / Node 18+ global) — no polyfill.
const IS_SERVER = typeof window === 'undefined'

const transformHeaders = headers => {
  const newHeaders = {}
  const keep = [
    'accept',
    'x-real-ip',
    'x-forwarded-for',
    'accept',
    'cookie',
    'user-agent',
    'accept-language',
    'cache-control',
  ]
  for (let i = 0; i < keep.length; i += 1) {
    if ({}.hasOwnProperty.call(headers, keep[i])) {
      newHeaders[keep[i]] = headers[keep[i]]
    }
  }
  newHeaders['x-isomorphic-proxy'] = 'true'
  return newHeaders
}

const cache = {}

/**
 * Fetch data from a given URL using either fetch in a browser or directly via Node when running on
 * the server.
 *
 * @param {String} url
 * @param {Object} [options] Optional
 * @param {String} [options.method] Default "GET"
 * @param {Object} [options.req]
 * @param {Object} [options.body]
 * @param {Number} [options.ttl]
 * @returns {Promise}
 */
export const getJSON = (url, inOptions) => {
  const options = inOptions || {}
  options.method = options.method || 'GET'
  let promise
  let cacheKey = null
  const requestUrl = url.startsWith('http') ? url : `${getGlobals().API_SERVER_HOST}${url}`
  const raw = !!options.raw

  if (IS_SERVER) {
    let newHeaders = {}
    if (options.req && options.req.headers) {
      newHeaders = transformHeaders(options.req.headers)
    } else if (options && options.headers) {
      newHeaders = transformHeaders(options.headers)
    }

    if (options.ttl) {
      cacheKey = requestUrl

      if (cache[cacheKey] && cache[cacheKey].expires > new Date()) {
        return new Promise((resolve, reject) => {
          resolve(raw ? cache[cacheKey].data : JSON.parse(cache[cacheKey].data))
        })
      }
    }

    promise = fetch(requestUrl, {
      method: options.method,
      body: options.body,
      headers: {
        ...newHeaders,
        ...(options.headers || {}),
      },
    })
  } else {
    promise = fetch(requestUrl, {
      method: options.method,
      credentials: 'include',
      body: options.body,
      headers: {
        Accept: raw ? null : 'application/json',
        cache: 'no-store',
        ...(options.headers || {}),
      },
    })
  }

  return promise.then(response => {
    if (response.status !== 200) {
      return response.text().then(text => {
        let json = null
        try {
          json = JSON.parse(text)
        } catch (e) {
          // console.log('Response is not valid JSON'); //, text);
        }
        const error = new Error({
          message: `Error: getJSON received wrong status. Expected 200, got ${response.status}`,
        })
        error.response = json
        error.statusCode = response.status
        // TODO: find proper solution for bubbeling up status codes if throwing error
        if (!IS_SERVER) {
          throw error
        }
      })
    }
    if (cacheKey !== null) {
      return response.text().then(text => {
        cache[cacheKey] = {
          expires: new Date(new Date().getTime() + options.ttl * 1000),
          data: text,
        }

        return new Promise((resolve, reject) => {
          resolve(raw ? text : JSON.parse(text))
        })
      })
    }
    return raw ? response.text() : response.json()
  })
}

/**
 * Post data to the server.
 *
 * @param {String} url
 * @param {Object} data
 * @returns {Promise}
 */
export const postData = (url, data) => getJSON(url, { method: 'POST', body: data })
export const postJSON = (url, data, options = {}) =>
  getJSON(url, {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
